import { useEffect, useState } from 'react';
import type { FileAttachmentReference } from '../../lib/integrations/storage/types';
import { captureException } from '../../lib/telemetry/posthog/frontend';

/** Review metadata and open customer-owned files with the reviewer's own Drive permissions. */
export function ResponseFiles({ responseId, token }: { responseId: string; token?: string }) {
  const [files, setFiles] = useState<FileAttachmentReference[]>([]);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!token) return undefined;
    const abort = new AbortController();
    setFiles([]); setError(false);
    void (async () => {
      try {
        const response = await fetch(`/api/responses/${encodeURIComponent(responseId)}/attachments`, { headers: { Authorization: `Bearer ${token}` }, signal: abort.signal });
        if (!response.ok) throw new Error('Files could not load');
        const result = await response.json() as { attachments: FileAttachmentReference[] };
        if (!abort.signal.aborted) setFiles(result.attachments.filter(item => item.state === 'completed'));
      } catch (failure) {
        if (!abort.signal.aborted) { setError(true); captureException(failure instanceof Error ? failure : String(failure), 'ResponseFiles.load', { responseId }); }
      }
    })();
    return () => abort.abort();
  }, [responseId, token, attempt]);
  if (!files.length && !error) return null;
  return <section aria-label="Collected files" className="rounded-2xl border border-border/50 bg-background/50 p-8">
    <h3 className="mb-4 text-sm font-medium">Files</h3>
    {error ? <div role="alert" className="text-sm">Files could not load. <button type="button" className="underline" onClick={() => setAttempt(value => value + 1)}>Retry</button></div> : null}
    <ul className="space-y-3">{files.map(file => <li key={file.id} className="flex items-center justify-between gap-4 text-sm">
      <div className="min-w-0"><p className="truncate" title={file.name}>{file.storedName || file.name}</p><p className="text-xs text-muted-foreground"><span>{file.mimeType}</span> · <span>{file.size < 1048576 ? `${Math.ceil(file.size / 1024)} KB` : `${(file.size / 1048576).toFixed(1)} MB`}</span></p></div>
      {file.provider === 'google' && file.externalFileId ? <a className="shrink-0 underline underline-offset-4" href={`https://drive.google.com/file/d/${encodeURIComponent(file.externalFileId)}/view`} target="_blank" rel="noopener noreferrer">Open in Drive</a> : <span>Unavailable</span>}
    </li>)}</ul>
  </section>;
}
